/**
 * CustomerForm — issue #28.
 *
 * Modal form used in two modes:
 *   - `mode: 'create'`     → POST /api/admin/customers
 *   - `mode: 'edit'`       → PATCH /api/admin/customers/:id (prefilled)
 *
 * The form is intentionally minimal in v1 — first name, last name, email,
 * phone. Additional fields (SMS-deals opt-out, source, household notes)
 * can be added without changing the call sites.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Alert, Button, Modal } from '@ferro/ui';
import type {
  CreateCustomerInput,
  CustomerDetail,
  CustomerSummary,
  UpdateCustomerInput,
} from '../../lib/api.js';

/**
 * #679 — optional starting values for create mode (the Quick Book walk-in
 * handoff carries the searched text into the matching field). Prefill only:
 * validation, trimming and the submit path are untouched, so desk-created
 * records keep the #556 incomplete-flag behavior byte-identical.
 */
export interface CustomerCreatePrefill {
  readonly firstName?: string;
  readonly lastName?: string;
  readonly email?: string;
  readonly phone?: string;
}

export type CustomerFormMode =
  | { kind: 'create'; prefill?: CustomerCreatePrefill }
  | { kind: 'edit'; customer: CustomerDetail };

export interface CustomerFormProps {
  readonly isOpen: boolean;
  readonly mode: CustomerFormMode;
  readonly onClose: () => void;
  readonly onCreated?: (customer: CustomerSummary) => void;
  readonly onUpdated?: (customer: CustomerSummary) => void;
  readonly onSubmitCreate?: (input: CreateCustomerInput) => Promise<CustomerSummary>;
  readonly onSubmitUpdate?: (input: UpdateCustomerInput) => Promise<CustomerSummary>;
}

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

function initialState(mode: CustomerFormMode): FormState {
  if (mode.kind === 'edit') {
    return {
      firstName: mode.customer.first_name,
      lastName: mode.customer.last_name,
      email: mode.customer.email,
      phone: mode.customer.phone,
    };
  }
  return {
    firstName: mode.prefill?.firstName ?? '',
    lastName: mode.prefill?.lastName ?? '',
    email: mode.prefill?.email ?? '',
    phone: mode.prefill?.phone ?? '',
  };
}

function diffForUpdate(initial: FormState, current: FormState): UpdateCustomerInput {
  const out: UpdateCustomerInput = {};
  if (current.firstName !== initial.firstName) out.firstName = current.firstName;
  if (current.lastName !== initial.lastName) out.lastName = current.lastName;
  if (current.email !== initial.email) out.email = current.email;
  if (current.phone !== initial.phone) out.phone = current.phone;
  return out;
}

export function CustomerForm({
  isOpen,
  mode,
  onClose,
  onCreated,
  onUpdated,
  onSubmitCreate,
  onSubmitUpdate,
}: CustomerFormProps): JSX.Element {
  const initial = useMemo(() => initialState(mode), [mode]);
  const [form, setForm] = useState<FormState>(initial);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setForm(initial);
      setError(null);
      setSubmitting(false);
    }
  }, [isOpen, initial]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (submitting) return;

    const trimmed: FormState = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim().toLowerCase(),
      phone: form.phone.trim(),
    };

    if (mode.kind === 'create') {
      if (
        !trimmed.firstName ||
        !trimmed.lastName ||
        !trimmed.email ||
        !trimmed.phone
      ) {
        setError('All fields are required.');
        return;
      }
    } else {
      const changes = diffForUpdate(initial, trimmed);
      if (Object.keys(changes).length === 0) {
        setError('No changes to save.');
        return;
      }
    }

    setError(null);
    setSubmitting(true);
    try {
      if (mode.kind === 'create') {
        if (!onSubmitCreate) throw new Error('onSubmitCreate is required for create mode');
        const created = await onSubmitCreate({
          firstName: trimmed.firstName,
          lastName: trimmed.lastName,
          email: trimmed.email,
          phone: trimmed.phone,
        });
        onCreated?.(created);
      } else {
        if (!onSubmitUpdate) throw new Error('onSubmitUpdate is required for edit mode');
        const changes = diffForUpdate(initial, trimmed);
        const updated = await onSubmitUpdate(changes);
        onUpdated?.(updated);
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save customer');
    } finally {
      setSubmitting(false);
    }
  };

  const title = mode.kind === 'create' ? 'Add customer' : 'Edit customer';
  const submitLabel = mode.kind === 'create' ? 'Create customer' : 'Save changes';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="md">
      <form className="ferro-form" onSubmit={handleSubmit} noValidate>
        <div className="ferro-form__row">
          <label className="ferro-field">
            <span className="ferro-field__label">First name</span>
            <input
              type="text"
              name="firstName"
              autoComplete="given-name"
              className="ferro-field__input"
              value={form.firstName}
              onChange={(e) => setForm({ ...form, firstName: e.target.value })}
              required
            />
          </label>
          <label className="ferro-field">
            <span className="ferro-field__label">Last name</span>
            <input
              type="text"
              name="lastName"
              autoComplete="family-name"
              className="ferro-field__input"
              value={form.lastName}
              onChange={(e) => setForm({ ...form, lastName: e.target.value })}
              required
            />
          </label>
        </div>

        <label className="ferro-field">
          <span className="ferro-field__label">Email</span>
          <input
            type="email"
            name="email"
            autoComplete="email"
            className="ferro-field__input"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
          />
        </label>

        <label className="ferro-field">
          <span className="ferro-field__label">Phone</span>
          <input
            type="tel"
            name="phone"
            autoComplete="tel"
            className="ferro-field__input"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            required
          />
        </label>

        {error ? <Alert variant="error">{error}</Alert> : null}

        <div className="ferro-form__actions">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : submitLabel}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
